<script setup lang="ts">
import { ref, watch } from 'vue';
import Step1 from './steps/Step1.vue';
import Step2 from './steps/Step2.vue';
import Step3 from './steps/Step3.vue';
import Step4 from './steps/Step4.vue';

interface ConstructionStep {
  title: string;
  desc: string;
  typed: string;
  placeholder: string;
  ghost: string;
  showInsight: boolean;
  expanded: boolean;
  resolved: boolean;
}

interface InsightResult {
  type: 'users' | 'user' | 'calendar';
  title: string;
  meta: string;
  showExpand?: boolean;
}

interface InsightGroup {
  char: string;
  results: InsightResult[];
}

const props = defineProps<{
  activeStep: number;
  steps: ConstructionStep[];
}>();

const emit = defineEmits<{
  (e: 'scrollToEditor'): void;
  (e: 'update:activeStep', step: number): void;
}>();

const displayedText = ref("");
const isTyping = ref(false);
let typingInterval: any = null;
let delayTimeout: any = null;

const startTypingAnimation = (targetText: string, startText: string = "", delay: number = 100) => {
  isTyping.value = startText !== "";
  displayedText.value = startText;
  clearInterval(typingInterval);
  clearTimeout(delayTimeout);
  
  const performTyping = (startIndex: number) => {
    isTyping.value = true;
    let i = startIndex;
    typingInterval = setInterval(() => {
      if (i < targetText.length) {
        displayedText.value += targetText.charAt(i);
        i++;
      } else {
        clearInterval(typingInterval);
        isTyping.value = false;
      }
    }, delay);
  };

  if (startText) {
    performTyping(startText.length);
  } else {
    delayTimeout = setTimeout(() => {
      performTyping(0);
    }, 500);
  }
};

const insightResults: InsightGroup[] = [
  { char: 'A', results: [
    { type: 'user', title: 'Adu', meta: '' },
    { type: 'user', title: 'Agu', meta: '' },
    { type: 'user', title: 'Aké', meta: '' },
    { type: 'user', title: 'Ali', meta: '' },
    { type: 'user', title: 'Ali', meta: '' },
    { type: 'user', title: 'Abdi', meta: '' }
  ]},
  { char: 'Ar', results: [
    { type: 'user', title: 'Arce', meta: '' },
    { type: 'user', title: 'Arcos', meta: '' },
    { type: 'user', title: 'Arcus', meta: '' },
    { type: 'user', title: 'Aremu', meta: '' },
    { type: 'user', title: 'Arena', meta: '' },
    { type: 'user', title: 'Areso', meta: '' }
  ]},
  { char: 'Ars', results: [
    { type: 'users', title: 'Arsenal', meta: '', showExpand: true },
    { type: 'user', title: 'Arsen Zakharyan', meta: '' },
    { type: 'calendar', title: 'Arsenal - PSG', meta: 'Sat 30th 17:00' },
    { type: 'calendar', title: 'Arsenal - Burnley', meta: 'Mon 18th 20:00' }
  ]},
  { char: 'Arsena', results: [
    { type: 'users', title: 'Arsenal', meta: '', showExpand: true },
    { type: 'calendar', title: 'Arsenal - PSG', meta: 'Sat 30th 17:00' },
    { type: 'calendar', title: 'Arsenal - Burnley', meta: 'Mon 18th 20:00' },
    { type: 'calendar', title: 'Arsenal - West Ham', meta: 'Sun 16:30' }
  ]}
];

const getCurrentResults = () => {
  const text = displayedText.value.toLowerCase();
  if (text.startsWith('arsena')) return insightResults[3]?.results || [];
  if (text.startsWith('ars')) return insightResults[2]?.results || [];
  if (text.startsWith('ar')) return insightResults[1]?.results || [];
  if (text.startsWith('a')) return insightResults[0]?.results || [];
  return [];
};

watch(() => props.activeStep, (newStep) => {
  const step = props.steps[newStep];
  if (step && step.typed) {
    const startText = newStep === 3 ? "Arsenal and " : "";
    const delay = newStep === 1 ? 500 : 100;
    startTypingAnimation(step.typed, startText, delay);
  } else {
    displayedText.value = "";
    isTyping.value = false;
  }
}, { immediate: true });
</script>

<template>
  <div class="app-mock-container glass relative">
    <transition name="fade-up" mode="out-in">
      <Step1 
        v-if="activeStep === 0" 
        :displayedText="displayedText"
        :isTyping="isTyping"
        :step="steps[activeStep]!"
        @scrollToEditor="emit('scrollToEditor')"
      />
      <Step2
        v-else-if="activeStep === 1"
        :displayedText="displayedText"
        :isTyping="isTyping"
        :step="steps[activeStep]!"
        :results="getCurrentResults()"
        @update:activeStep="emit('update:activeStep', $event)"
      />
      <Step3
        v-else-if="activeStep === 2"
        :displayedText="displayedText"
        :isTyping="isTyping"
        :step="steps[activeStep]!"
        :results="getCurrentResults()"
      />
      <Step4
        v-else-if="activeStep === 3"
        :displayedText="displayedText"
        :isTyping="isTyping"
        :step="steps[activeStep]!"
        @scrollToEditor="emit('scrollToEditor')"
      />
    </transition>
  </div>
</template>

<style scoped>
.app-mock-container {
  width: 100%;
  max-width: 800px;
  height: 650px;
  background: #09090b;
  border-radius: 32px;
  border: 1px solid rgba(255,255,255,0.1);
  box-shadow: 0 40px 100px rgba(0,0,0,0.5);
  position: relative;
  padding: 24px;
  overflow: hidden;
  box-sizing: border-box;
}

@media (max-width: 768px) {
  .app-mock-container { border-radius: 0; padding: 16px; height: 550px; }
}

.fade-up-enter-active { animation: fadeInUp 0.4s ease; }
.fade-up-leave-active { animation: fadeOutDown 0.3s ease; }
@keyframes fadeInUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
@keyframes fadeOutDown { from { opacity: 1; transform: translateY(0); } to { opacity: 0; transform: translateY(10px); } }
</style>
